/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- focused overflow regions let keyboard users pan wide data tables */
import type React from 'react'
import { useTranslation } from 'react-i18next'

import type { UseDataTableOptions } from '@/lib/table/useDataTable'
import { useDataTable } from '@/lib/table/useDataTable'

export type { DataTableColumn } from '@/lib/table/useDataTable'

export interface DataTableProps<T> extends UseDataTableOptions<T> {
  /** Message shown when there are no rows */
  emptyMessage?: string
  /** Accessible label for the table (used when no visible caption is provided) */
  ariaLabel?: string
  /** Optional table caption rendered inside `<caption>` */
  caption?: React.ReactNode
  /** Additional class name for the `<table>` element */
  tableClassName?: string
  /** Optional row-level class name factory */
  rowClassName?: (row: T, index: number) => string
  /** Optional extra class name for the header cell, by column key */
  headerClassNameByKey?: Record<string, string | undefined>
  /**
   * Class name for data cells in a column, by column key.
   * Can be a string or a function of the row and index.
   */
  cellClassNameByKey?: Record<string, string | ((row: T, index: number) => string) | undefined>
}

function getCellValue<T>(row: T, key: keyof T | string): React.ReactNode {
  const value = (row as Record<string, unknown>)[key as string]
  return value !== undefined && value !== null ? String(value) : ''
}

export function DataTable<T>({
  columns,
  data,
  getRowKey,
  emptyMessage,
  ariaLabel,
  caption,
  tableClassName,
  rowClassName,
  headerClassNameByKey,
  cellClassNameByKey,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const table = useDataTable<T>({ columns, data, getRowKey })
  const tableName =
    ariaLabel ?? (typeof caption === 'string' ? caption : t('table.defaultAriaLabel'))

  return (
    // A focused scroll region lets keyboard users pan wide, two-dimensional tables.
    <div
      className="max-w-full overflow-x-auto rounded border border-border-gray"
      role="region"
      aria-label={t('table.scrollRegionLabel', { name: tableName })}
      tabIndex={0}
    >
      <table
        className={`min-w-full divide-y divide-gray-200 text-left text-sm ${tableClassName ?? ''}`}
        aria-label={caption ? undefined : tableName}
      >
        {caption ? (
          <caption className="px-4 py-2 text-left text-gray-700">{caption}</caption>
        ) : null}
        <thead className="bg-gray-50">
          <tr>
            {table.columns.map((col) => {
              const headerClassFromMap = headerClassNameByKey?.[String(col.key)] ?? ''
              return (
                <th
                  key={col.key as string}
                  scope="col"
                  aria-sort={col.ariaSort}
                  className={`px-4 py-3 font-medium whitespace-nowrap text-gray-700 ${headerClassFromMap}`}
                >
                  {col.header as React.ReactNode}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {table.isEmpty ? (
            <tr>
              <td colSpan={table.columns.length} className="px-4 py-8 text-center text-gray-500">
                {emptyMessage ?? t('table.emptyDefault')}
              </td>
            </tr>
          ) : (
            table.rows.map((rowWithMeta, index) => {
              const row = rowWithMeta.original
              const baseRowClass = 'hover:bg-gray-50'
              const extraRowClass = rowClassName ? rowClassName(row, index) : ''
              const combinedRowClass = baseRowClass + (extraRowClass ? ` ${extraRowClass}` : '')

              return (
                <tr key={rowWithMeta.id} className={combinedRowClass}>
                  {table.columns.map((col) => {
                    const baseCellClass = 'px-4 py-3 text-gray-900'
                    const cellClassForKey = cellClassNameByKey?.[String(col.key)]
                    const extraCellClass =
                      typeof cellClassForKey === 'function'
                        ? cellClassForKey(row, index)
                        : (cellClassForKey ?? '')
                    const combinedCellClass =
                      baseCellClass + (extraCellClass ? ` ${extraCellClass}` : '')

                    return (
                      <td key={col.key as string} className={combinedCellClass}>
                        {col.render
                          ? (col.render(row, index) as React.ReactNode)
                          : getCellValue(row, col.key)}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
